import { ApiError, NetworkError } from '../lib/api';
import type { GenerateReason } from '../lib/types';

/**
 * What the customer is told when a generation does not produce an image.
 *
 * Three rules this encodes, all of which matter more than they look:
 *
 * 1. `quota_exceeded` must never be shown for anything else. A store owner who
 *    sees "limit reached" will ring about a quota problem that does not exist.
 *
 * 2. `credentials_error` and `configuration_error` share a string on purpose.
 *    The customer cannot act differently on either, and the difference — one
 *    store's key versus every store's master key — only matters to whoever is
 *    debugging. Do not invent a second wording to separate them on screen.
 *
 * 3. `already_generating` is not an error, and is **no longer rendered at all**.
 *    The queue intercepts that reason and keeps the card spinning, because the
 *    only honest answer to "is this working?" is visible progress — a line of
 *    text reading "already working on this garment" told the customer off for
 *    tapping something that had plainly not finished. The entry survives here
 *    because `GenerateReason` is exhaustive and the API can still return it.
 */
export interface ReasonPresentation {
  message: string;
  /** `waiting` renders as in-progress, not as a failure. */
  kind: 'error' | 'waiting';
  /** Whether offering a retry on this card makes sense. */
  retryable: boolean;
}

const PRESENTATIONS: Record<GenerateReason, ReasonPresentation> = {
  quota_exceeded: {
    message: 'Store limit reached — staff should contact the provider.',
    kind: 'error',
    retryable: false,
  },
  garment_unavailable: {
    message: 'This one is unavailable — try a different garment.',
    kind: 'error',
    retryable: false,
  },
  /**
   * Ad-hoc only. **Not retryable**, and that is the honest answer rather than a
   * cautious one: the category list the picker was drawn from is stale, so
   * re-sending the identical request fails identically. Reopening the sheet
   * refetches the list, which is the action the message points at.
   */
  unknown_category: {
    message: 'That garment type is no longer set up — pick another.',
    kind: 'error',
    retryable: false,
  },
  already_generating: {
    message: 'Already working on this garment.',
    kind: 'waiting',
    retryable: false,
  },
  credentials_error: {
    message: 'Setup issue — please tell the shop.',
    kind: 'error',
    retryable: false,
  },
  configuration_error: {
    message: 'Setup issue — please tell the shop.',
    kind: 'error',
    retryable: false,
  },
  generation_failed: {
    message: "That photo didn't work — try another.",
    kind: 'error',
    retryable: true,
  },
  generation_timeout: {
    message: 'Took too long — try again.',
    kind: 'error',
    retryable: true,
  },
  upstream_throttled: {
    message: 'Busy right now — try again in a minute.',
    kind: 'error',
    retryable: true,
  },
};

export function presentReason(reason: GenerateReason): ReasonPresentation {
  return PRESENTATIONS[reason];
}

/**
 * The request never got an answer — backgrounded app, wifi drop, or iOS purging
 * the page. Distinct from a server-side failure because the generation may well
 * have succeeded and been billed; there is no job id to poll, so retrying is
 * the only option and it may cost a second image.
 */
export const TRANSPORT_FAILURE: ReasonPresentation = {
  message: 'Lost connection — try again.',
  kind: 'error',
  retryable: true,
};

/**
 * What a failed photo upload says, and to whom.
 *
 * ⚠️ **This one needs two audiences, unlike every presentation above it.** The
 * upload goes device→R2 directly, so a failure appears in *no* log anywhere —
 * the API's last word on the subject is `upload_url_issued`, and the bytes never
 * reach it. A single friendly string ("The photo could not be uploaded") is
 * therefore the whole of the evidence, and three unrelated causes were rendering
 * it identically:
 *
 *   the bucket has no CORS policy   →  the browser blocks the PUT outright
 *   the signed type ≠ the sent one  →  `403 SignatureDoesNotMatch`
 *   the url is older than 30 min    →  `403`, a different `<Code>`
 *
 * So `message` stays customer-facing and `detail` carries the code for whoever
 * is standing behind them. `detail` is deliberately not prose — it is the string
 * to search this file and the R2 dashboard for.
 */
export interface UploadFailure {
  message: string;
  detail: string;
}

export function presentUploadFailure(err: unknown): UploadFailure {
  if (err instanceof NetworkError) {
    /**
     * ⚠️ Ambiguous by construction — the browser does not tell script whether a
     * request died on the wire or was refused by CORS. Naming both is honest;
     * naming one would send someone to check the wifi for a bucket policy.
     */
    return {
      message: 'The photo could not be sent. Check the connection and try again.',
      detail: 'upload_unreachable — device offline, or the storage bucket does not allow uploads from this site (CORS)',
    };
  }

  if (err instanceof ApiError) {
    if (err.code === 'token_unavailable') {
      /**
       * The session could not be refreshed. Common cause on a phone: the app was
       * backgrounded to pick a photo out of the gallery and came back to an
       * expired token on a connection that was not there for the refresh.
       */
      return {
        message: 'The session expired while you were picking a photo. Please try again.',
        detail: `token_unavailable — ${err.message}`,
      };
    }

    if (err.code === 'SignatureDoesNotMatch') {
      // Not retryable by the customer: every retry re-signs the same mismatched
      // type and fails the same way. Saying "try again" would be a lie.
      return {
        message: 'This photo could not be accepted. Please tell the shop.',
        detail: 'SignatureDoesNotMatch — the uploaded Content-Type differs from the one the url was signed for',
      };
    }

    if (err.code === 'AccessDenied' || err.code === 'RequestTimeTooSkewed') {
      return {
        message: 'The upload took too long. Please take the photo again.',
        detail: `${err.code} — the upload link had expired (they last 30 minutes)`,
      };
    }

    return {
      message: 'The photo could not be uploaded. Please try again.',
      detail: `${err.code ?? 'unknown'} — storage returned ${err.status}`,
    };
  }

  /**
   * ⚠️ **Must name the error.** This branch used to read "see the browser
   * console", which on the device that actually hits it — a phone on a shop
   * counter — is advice nobody can take. Anything reaching here is by definition
   * a failure mode not yet accounted for, so the error's own type and message
   * are the only evidence that will ever exist.
   */
  const described =
    err instanceof Error ? `${err.name}: ${err.message}` : `${typeof err}: ${String(err)}`;

  return {
    message: 'The photo could not be uploaded. Please try again.',
    detail: `unexpected — ${described}`,
  };
}
